/**
 * Conditional-formatting rules as the Rules Manager edits them (Excel / DevExpress
 * "Conditional Formatting Rules Manager" parity): each worksheet has ONE ordered rule
 * list — first = evaluated first — and every rule has a range, a definition, a format and
 * Stop If True. The workbook's own rules are loaded as-is; `toEdits` turns the edited
 * list back into the minimal server edits (identifying file rules by their ORIGINAL
 * priority). Pure functions — unit-tested.
 */
import type { ConditionalFormatEdit } from './sheetEdits';
import { parseA1, type WorkbookCfRule } from './templateWorkbook';

/** Rule types the builder can create and edit; others (data bars, colour scales, …) can be reordered / deleted. */
export const EDITABLE_CF_TYPES = new Set(['cellIs', 'containsText', 'expression']);

export const CF_OPERATORS: { id: string; label: string }[] = [
  { id: 'greaterThan', label: 'Greater than' },
  { id: 'greaterThanOrEqual', label: 'Greater than or equal to' },
  { id: 'lessThan', label: 'Less than' },
  { id: 'lessThanOrEqual', label: 'Less than or equal to' },
  { id: 'equal', label: 'Equal to' },
  { id: 'notEqual', label: 'Not equal to' },
  { id: 'between', label: 'Between' },
  { id: 'notBetween', label: 'Not between' },
];
const OP_LABEL: Record<string, string> = Object.fromEntries(CF_OPERATORS.map(o => [o.id, o.label]));

/** The part of a rule the New / Edit Rule dialog edits. */
export interface CfDefinition {
  range: string;
  type: string;
  operator: string | null;
  formula1: string | null;
  formula2: string | null;
  text: string | null;
  fontColor: string | null;
  fillColor: string | null;
  bold: boolean;
}

export interface CfRule extends CfDefinition {
  /** stable key while the manager is open */
  key: string;
  sheetIndex: number;
  /** the rule's priority in the FILE; null = added in this session */
  origPriority: number | null;
  stopIfTrue: boolean;
}

let seq = 0;

/** The workbook's rules, each sheet in evaluation order. */
export function rulesFromWorkbook(rules: WorkbookCfRule[]): CfRule[] {
  return [...rules]
    .sort((a, b) => a.sheetIndex - b.sheetIndex || a.priority - b.priority)
    .map(r => ({
      key: `f${r.sheetIndex}-${r.priority}`, sheetIndex: r.sheetIndex, origPriority: r.priority,
      range: r.range, type: r.type, operator: r.operator,
      formula1: r.formulas[0] ?? null, formula2: r.formulas[1] ?? null, text: r.text,
      fontColor: r.fontColor, fillColor: r.fillColor, bold: r.bold, stopIfTrue: r.stopIfTrue,
    }));
}

export function newRule(sheetIndex: number, def: CfDefinition): CfRule {
  return { ...def, key: `n${++seq}`, sheetIndex, origPriority: null, stopIfTrue: false };
}

/** Excel-style rule text: "Cell value greater than 120", "Formula: =G8<>\"\"", … */
export function describeRule(r: Pick<CfDefinition, 'type' | 'operator' | 'formula1' | 'formula2' | 'text'>): string {
  const f = (s: string | null) => (s ?? '').replace(/^=/, '');
  switch (r.type) {
    case 'cellIs':
      return `Cell value ${(OP_LABEL[r.operator ?? ''] ?? r.operator ?? '').toLowerCase()} ${f(r.formula1)}${r.operator === 'between' || r.operator === 'notBetween' ? ` and ${f(r.formula2)}` : ''}`;
    case 'containsText': return `Cell value contains "${r.text ?? ''}"`;
    case 'expression': return `Formula: =${f(r.formula1)}`;
    case 'containsBlanks': return 'Blanks';
    case 'notContainsBlanks': return 'No blanks';
    case 'containsErrors': return 'Errors';
    case 'notContainsErrors': return 'No errors';
    case 'dataBar': return 'Data bar';
    case 'colorScale': return 'Graded colour scale';
    case 'iconSet': return 'Icon set';
    case 'top10': return 'Top / bottom values';
    case 'aboveAverage': return 'Above / below average';
    case 'duplicateValues': return 'Duplicate values';
    case 'uniqueValues': return 'Unique values';
    default: return r.type || '(rule)';
  }
}

/** "Q9:Q20 S9" → "=$Q$9:$Q$20,$S$9" (how Excel shows "Applies to"). */
export function absoluteSqref(sqref: string): string {
  const abs = (ref: string) => ref.replace(/^([A-Z]+)(\d+)$/i, (_, c: string, n: string) => `$${c.toUpperCase()}$${n}`);
  return '=' + sqref.trim().split(/\s+/).filter(Boolean).map(r => r.split(':').map(abs).join(':')).join(',');
}

/** "=$Q$9:$Q$20,$S$9" / "Q9:Q20 S9" → "Q9:Q20 S9"; null when invalid. */
export function normalizeSqref(input: string): string | null {
  const parts = input.trim().replace(/^=/, '').replace(/\$/g, '').split(/[\s,]+/).filter(Boolean).map(p => p.toUpperCase());
  if (!parts.length) return null;
  for (const p of parts) {
    const [a, b] = p.split(':');
    if (!parseA1(a) || (b !== undefined && !parseA1(b)) || p.split(':').length > 2) return null;
  }
  return parts.join(' ');
}

export interface CellBox { startRow: number; startCol: number; endRow: number; endCol: number }

/** Does any range of the sqref overlap the selection? (0-based selection) */
export function sqrefIntersects(sqref: string, sel: CellBox): boolean {
  return sqref.split(/\s+/).filter(Boolean).some(part => {
    const [a, b] = part.split(':');
    const p = parseA1(a), q = parseA1(b ?? a);
    if (!p || !q) return false;
    const r1 = Math.min(p.row, q.row), r2 = Math.max(p.row, q.row), c1 = Math.min(p.col, q.col), c2 = Math.max(p.col, q.col);
    return r1 <= sel.endRow && sel.startRow <= r2 && c1 <= sel.endCol && sel.startCol <= c2;
  });
}

const DEF_KEYS: (keyof CfDefinition)[] = ['type', 'operator', 'formula1', 'formula2', 'text', 'fontColor', 'fillColor', 'bold'];
const sameDef = (a: CfDefinition, b: CfDefinition) => DEF_KEYS.every(k => (a[k] ?? null) === (b[k] ?? null));
const defFields = (r: CfDefinition): Partial<ConditionalFormatEdit> => ({
  type: r.type as ConditionalFormatEdit['type'],
  ...(r.operator ? { operator: r.operator } : {}),
  ...(r.formula1 != null ? { formula1: r.formula1 } : {}),
  ...(r.formula2 != null ? { formula2: r.formula2 } : {}),
  ...(r.text != null ? { text: r.text } : {}),
  ...(r.fontColor ? { fontColor: r.fontColor } : {}),
  ...(r.fillColor ? { fillColor: r.fillColor } : {}),
  ...(r.bold ? { bold: true } : {}),
});

/**
 * The server edits that turn `original` into `model`. Rules keep their file priority
 * unless the sheet's order changed (a move, an add, …) — then every rule on that sheet
 * is renumbered 1..n in list order, so the file order matches what the designer sees.
 */
export function toEdits(original: CfRule[], model: CfRule[]): ConditionalFormatEdit[] {
  const out: ConditionalFormatEdit[] = [];
  const sheets = new Set([...original, ...model].map(r => r.sheetIndex));
  for (const sheet of [...sheets].sort((a, b) => a - b)) {
    const orig = original.filter(r => r.sheetIndex === sheet);
    const mod = model.filter(r => r.sheetIndex === sheet);
    const origByKey = new Map(orig.map(r => [r.key, r]));
    const keep = new Set(mod.map(r => r.key));

    for (const o of orig) if (!keep.has(o.key))
      out.push({ sheetIndex: sheet, action: 'remove', priority: o.origPriority!, range: o.range, oldDescription: describeRule(o) });

    const keptOrder = mod.filter(r => r.origPriority != null).map(r => r.key).join('|');
    const fileOrder = orig.filter(r => keep.has(r.key)).map(r => r.key).join('|');
    const renumber = keptOrder !== fileOrder || mod.some(r => r.origPriority == null);

    mod.forEach((r, i) => {
      const newPriority = renumber ? i + 1 : undefined;
      const o = origByKey.get(r.key);
      if (!o) {
        out.push({ sheetIndex: sheet, action: 'add', range: r.range, ...defFields(r),
          ...(r.stopIfTrue ? { stopIfTrue: true } : {}), ...(newPriority ? { newPriority } : {}), description: describeRule(r) });
        return;
      }
      const edit: ConditionalFormatEdit = { sheetIndex: sheet, action: 'update', priority: o.origPriority! };
      let changed = false;
      if (!sameDef(o, r)) { Object.assign(edit, defFields(r)); changed = true; }
      if (o.range !== r.range) { edit.range = r.range; changed = true; }
      if (o.stopIfTrue !== r.stopIfTrue) { edit.stopIfTrue = r.stopIfTrue; changed = true; }
      if (newPriority && newPriority !== o.origPriority) { edit.newPriority = newPriority; changed = true; }
      if (!changed) return;
      edit.description = describeRule(r);
      edit.oldDescription = describeRule(o);
      out.push(edit);
    });
  }
  return out;
}
